import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { applyBrand, BrandBlock, BrandingContext } from "@/contexts/BrandingProvider";

interface PreviewBrandingContextValue {
  branding: BrandBlock | null;
  isPreviewMode: boolean;
}

const PreviewBrandingInternalContext = createContext<PreviewBrandingContextValue>({
  branding: null,
  isPreviewMode: false,
});

export const usePreviewBranding = () => useContext(PreviewBrandingInternalContext);
export const usePreviewMode = () => useContext(PreviewBrandingInternalContext).isPreviewMode;

// WHY env-driven allowlist: postMessage from cross-origin iframes is the trust boundary
// for the live-preview channel. Any non-allowlisted origin silently drops, so a malicious
// page that embeds the preview route cannot mutate brand state. The env var lets dev/prod
// pin their respective dashboards without rebuilds for new owners.
function allowedOrigins(): string[] {
  const raw = (import.meta.env.VITE_DASHBOARD_ORIGIN as string | undefined) ?? "";
  return raw
    .split(",")
    .map((s: string) => s.trim())
    .filter((s: string) => s.length > 0);
}

function parseBrandFromQuery(): BrandBlock | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const encoded = params.get("brand");
  if (!encoded) return null;
  try {
    const decoded = atob(encoded);
    const parsed = JSON.parse(decoded);
    if (typeof parsed === "object" && parsed !== null) {
      return parsed as BrandBlock;
    }
  } catch {
    // malformed — fall through to default
  }
  return null;
}

export function PreviewBrandingProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<BrandBlock | null>(() => {
    const fromQuery = parseBrandFromQuery();
    if (fromQuery) {
      applyBrand(fromQuery);
    }
    return fromQuery;
  });

  useEffect(() => {
    const origins = allowedOrigins();

    function postReady() {
      if (origins.length === 0) return;
      // WHY targetOrigin loop: window.parent.postMessage can target only one origin per
      // call. We post READY to each allowlisted origin so the dashboard receives it
      // regardless of which env hosted the iframe.
      for (const origin of origins) {
        try {
          window.parent.postMessage({ type: "PREVIEW_READY" }, origin);
        } catch {
          /* ignore — cross-origin restrictions are expected */
        }
      }
    }

    function handleMessage(event: MessageEvent) {
      if (!origins.includes(event.origin)) return;
      const data = event.data as { type?: string; payload?: BrandBlock };
      if (!data || data.type !== "BRAND_UPDATE" || !data.payload) return;
      const next = data.payload;
      applyBrand(next);
      setBranding(next);
    }

    window.addEventListener("message", handleMessage);
    // WHY post READY after attaching listener: the dashboard awaits the READY signal
    // before pushing the initial BRAND_UPDATE. If we posted first and the listener
    // weren't attached, we'd race the response.
    postReady();

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  return (
    <BrandingContext.Provider
      value={{
        branding,
        isLoading: false,
        isBranded: branding !== null,
        isRedirecting: false,
      }}
    >
      <PreviewBrandingInternalContext.Provider value={{ branding, isPreviewMode: true }}>
        {children}
      </PreviewBrandingInternalContext.Provider>
    </BrandingContext.Provider>
  );
}
